/**
 * Email sign-in links and the funnel hand-off they carry (backend: core/email_link_signin.py).
 *
 * On iPhone, Google sign-in inside Instagram / Facebook's in-app browser comes back two times in three: that
 * browser has no saved Google login, and Meta blocks every way to open Safari from a page. So on the sign-in step
 * an in-app browser also offers "email me a sign-in link", and a tip points at the app's own ••• menu ("Open in
 * browser").
 *
 * Either way the parent continues in another browser, which has none of this tab's storage. The funnel's
 * progress (its own storage keys + the character picture) is saved to the backend as a hand-off, and its id rides
 * in the URL FRAGMENT (`#fh=`), with the link's one-time token (`#pml=`). A fragment never reaches a server, so it
 * stays out of CDN logs and Referer headers; the landing gate reads it and clears it before anything else.
 */
import logger from './logger';
import { adClickReturnParam } from './adAttribution';

export const TOKEN_PARAM = 'pml';
export const HANDOFF_PARAM = 'fh';
const PREVIEW_KEY = 'rc_funnel_preview_img';
const SAVED_AT_KEY = 'pixley_funnel_saved_at';
const KEY_RE = /^(rc_funnel_|pixley_funnel_)[a-z0-9_]{1,48}$/;
const HANDOFF_ID_RE = /^[A-Za-z0-9_-]{32}$/;
const TOKEN_RE = /^[A-Za-z0-9_-]{20,200}$/;
const URL_PARAMS = ['variant', 'funnel_redirect_uri', 'funnel_state'];

/** {token, handoffId} from the current URL's fragment (either may be null). */
export function readLinkFragment(hash = typeof window !== 'undefined' ? window.location.hash : '') {
  const params = new URLSearchParams((hash || '').replace(/^#/, ''));
  const token = params.get(TOKEN_PARAM);
  const handoffId = params.get(HANDOFF_PARAM);
  return {
    token: token && TOKEN_RE.test(token) ? token : null,
    handoffId: handoffId && HANDOFF_ID_RE.test(handoffId) ? handoffId : null,
  };
}

/** Drop the fragment from the address bar (keeps path and query). */
export function clearLinkFragment() {
  try {
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', pathname + search);
  } catch {
    /* nothing to clear */
  }
}

const MADE_HERE_KEY = 'pixley_handoff_made_here';

/** Whether this tab made the hand-off (a reload of the in-app tab must not "restore" its own progress over itself). */
export function handoffMadeHere(handoffId) {
  try { return !!handoffId && sessionStorage.getItem(MADE_HERE_KEY) === handoffId; } catch { return false; }
}

/** Put a hand-off id in the fragment, so Instagram's "Open in browser" carries it to the real browser. */
export function setHandoffFragment(handoffId) {
  try { sessionStorage.setItem(MADE_HERE_KEY, handoffId); } catch { /* blocked storage */ }
  try {
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, '', `${pathname}${search}#${HANDOFF_PARAM}=${handoffId}`);
  } catch {
    /* best effort */
  }
}

function dataUrlToBlob(dataUrl) {
  const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl || '');
  if (!m) return null;
  const bytes = atob(m[2]);
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) out[i] = bytes.charCodeAt(i);
  return new Blob([out], { type: m[1] });
}

/**
 * What this tab has made so far: {payload: {keys, params}, image: Blob|null}. The funnel's own storage keys only
 * (the backend drops anything else); the character picture travels as a file, not a key. characterName, when
 * known, is only for the email's wording.
 */
export function collectHandoff({ characterName } = {}) {
  const keys = {};
  let image = null;
  try {
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (!k || !KEY_RE.test(k)) continue;
      const v = sessionStorage.getItem(k);
      if (k === PREVIEW_KEY) {
        image = dataUrlToBlob(v);
      } else if (typeof v === 'string' && v.length <= 4000) {
        keys[k] = v;
      }
    }
  } catch {
    /* blocked storage: nothing to carry */
  }
  if (characterName) keys.pixley_funnel_character_name = String(characterName).slice(0, 40);
  const params = {};
  try {
    const search = new URLSearchParams(window.location.search);
    URL_PARAMS.forEach((p) => {
      const v = search.get(p);
      if (v) params[p] = v;
    });
  } catch {
    /* no params */
  }
  const adc = adClickReturnParam();
  if (adc) params.adc = adc;
  return { payload: { keys, params }, image };
}

/**
 * Write a loaded hand-off into this browser's storage, as the funnel itself would have (both copies, so a reload
 * resumes too). Returns the query string the funnel page should open with (its URL params + the ad click).
 */
export function restoreHandoff({ payload, image } = {}) {
  const keys = (payload && payload.keys) || {};
  const set = (k, v) => {
    try { sessionStorage.setItem(k, v); } catch { /* quota */ }
    try { localStorage.setItem(k, v); } catch { /* quota: the tab copy still works */ }
  };
  Object.entries(keys).forEach(([k, v]) => {
    if (KEY_RE.test(k) && typeof v === 'string') set(k, v);
  });
  if (typeof image === 'string' && image.startsWith('data:image/')) set(PREVIEW_KEY, image);
  try { localStorage.setItem(SAVED_AT_KEY, String(Date.now())); } catch { /* blocked */ }

  const query = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const params = (payload && payload.params) || {};
  [...URL_PARAMS, 'adc'].forEach((p) => {
    if (params[p] && !query.get(p)) query.set(p, params[p]);
  });
  const qs = query.toString();
  logger.log('Funnel hand-off restored', { keys: Object.keys(keys).length, picture: !!image });
  return qs ? `?${qs}` : '';
}

/** The ••• menu wording for this phone (iOS apps use •••, Android apps ⋮). */
export function browserMenuLabel(ua = typeof navigator !== 'undefined' ? navigator.userAgent : '') {
  return /Android/.test(ua) ? '⋮' : '•••';
}

/** A light shape check before asking the backend (the backend has the real one). */
export function looksLikeEmail(value) {
  const v = (value || '').trim();
  return v.length <= 254 && /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[A-Za-z]{2,}$/.test(v) && !v.includes('..');
}
